import React, {useState, useEffect} from "react";
import {useTranslation} from "react-i18next";
import { postFormData } from "../utils/api";
import {toast} from "react-toastify";
import LogoGold from "../assets/images/logo-gold.svg";

const PopupForm = () => {
    const {t} = useTranslation();
    const [isVisible, setIsVisible] = useState(false);
    const [formData, setFormData] = useState({
        Name: "",
        Email: "",
        Number: "",
        Request: "",
        Attachment: [],
    });
    const [imageUrls, setImageUrls] = useState([]);
    const [uploadStatus, setUploadStatus] = useState({});

    useEffect(() => {
        const hasSeenPopup = localStorage.getItem("hasSeenPopup");
        if (!hasSeenPopup) {
            setIsVisible(true);
        }
    }, []);

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleFileChange = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const uploadData = new FormData();
        for (let i = 0; i < files.length; i++) {
            uploadData.append("images", files[i]);
            setUploadStatus(prevStatus => ({
                ...prevStatus,
                [files[i].name]: "uploading"
            }));
        }

        try {
            const response = await fetch("https://api.britalbgroup.com/upload", {
                method: "POST",
                body: uploadData,
            });

            const data = await response.json();
            if (data.imageUrls) {
                setFormData(prevData => ({
                    ...prevData,
                    Attachment: [...prevData.Attachment, ...data.imageUrls.map(url => ({url}))]
                }));
                setImageUrls(prevUrls => [...prevUrls, ...data.imageUrls]);
                data.imageUrls.forEach((url, index) => {
                    setUploadStatus(prevStatus => ({
                        ...prevStatus,
                        [files[index].name]: "uploaded"
                    }));
                });
            }
        } catch (error) {
            toast.error(t("homepage.popupForm.errorMessage"));
        }
    };

    const handleRemoveImage = (index) => {
        const newImageUrls = imageUrls.filter((_, i) => i !== index);
        setImageUrls(newImageUrls);
        setFormData(prevData => ({
            ...prevData,
            Attachment: newImageUrls.map(url => ({url}))
        }));
    };

    const handleClose = () => {
        setIsVisible(false);
        localStorage.setItem("hasSeenPopup", "true");
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await postFormData(formData);
            toast.success(t("homepage.popupForm.successMessage"));
            localStorage.setItem("hasSeenPopup", "true");
            setIsVisible(false);
        } catch (err) {
            toast.error(t("homepage.popupForm.errorMessage"));
        }
    };

    if (!isVisible) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" style={{zIndex: 9999}}>
            <div className="flex flex-col bg-white p-6 rounded-md shadow-md w-96 md:w-[450px] overflow-y-auto">
                <div className="flex items-start justify-between relative border-b-2 border-gray-300 pb-2 mb-2">
                    <div className="flex flex-col">
                        <div className="flex items-center w-80 pb-3">
                            <img src={LogoGold} alt="BritAlb Logo" className="w-12 h-auto"/>
                            <h1 className="text-2xl font-normal ml-2 bg-gradient-to-r from-[#A07B1F] to-[#E3C651] bg-clip-text text-transparent">
                                BRITALB PROPERTIES
                            </h1>
                        </div>
                        <span className="text-sm text-justify">{t("homepage.popupForm.header")}</span>
                    </div>
                    <button
                        onClick={handleClose}
                        className="absolute top-0 right-0 text-gray-500 font-bold hover:bg-primary hover:text-secondary px-2 py-0.5 rounded-full text-xl"
                    >
                        &times;
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-gray-700">{t("contactUs.contact.form.name")}*</label>
                        <input
                            type="text"
                            name="Name"
                            value={formData.Name}
                            onChange={handleChange}
                            className="w-full p-2 border border-gray-300 rounded"
                            required
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-700">{t("contactUs.contact.form.email")}*</label>
                        <input
                            type="email"
                            name="Email"
                            value={formData.Email}
                            onChange={handleChange}
                            className="w-full p-2 border border-gray-300 rounded"
                            required
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-700">{t("contactUs.contact.form.phone")}*</label>
                        <input
                            type="tel"
                            name="Number"
                            value={formData.Number}
                            onChange={handleChange}
                            className="w-full p-2 border border-gray-300 rounded"
                            required
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-700">{t("homepage.popupForm.textarea")}</label>
                        <textarea
                            name="Request"
                            value={formData.Request}
                            onChange={handleChange}
                            rows="4"
                            className="w-full p-2 border border-gray-300 rounded"
                        />
                    </div>
                    <div className="mb-4">
                        <input
                            type="file"
                            name="images"
                            onChange={handleFileChange}
                            className="hidden"
                            id="fileInput"
                            multiple
                        />
                        <button
                            type="button"
                            onClick={() => document.getElementById("fileInput").click()}
                            className="w-full bg-primary text-white p-2 rounded hover:bg-secondary"
                        >
                            {t("homepage.popupForm.imageUpload")}
                        </button>
                        <div className="mt-2 flex flex-wrap justify-center">
                            {imageUrls.map((url, index) => (
                                <div key={index} className="relative">
                                    <img src={url} alt={`Uploaded ${index}`}
                                         className="w-14 h-14 object-cover m-1 border border-gray-500"/>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveImage(index)}
                                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center"
                                    >
                                        &times;
                                    </button>
                                </div>
                            ))}
                        </div>
                        {Object.keys(uploadStatus).map((fileName, index) => (
                            <div key={index} className="mt-2 text-center text-gray-700">
                                {uploadStatus[fileName] === "uploading" && `Uploading ${fileName}...`}
                            </div>
                        ))}
                    </div>
                    <button
                        type="submit"
                        className="w-full bg-primary text-white p-2 rounded hover:bg-secondary"
                    >
                        {t("homepage.popupForm.submit")}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default PopupForm;